import { usePathname } from 'next/navigation'
import { useCallback, useMemo, useState } from 'react'

import { Size } from '@syconium/little-miss-figgy'

import { useCartDialog, useCartInterstitialDialog } from '../../app/_providers/CartProvider.client'
import { useFeatureFlag } from '../../app/_providers/ExperimentationProvider.client'
import { usePortal } from '../../app/_providers/PortalProvider.client'
import { CartContainer } from '../../brunswick/containers/cart'
import { useWaitlistModal } from '../../components/pages/products/ProductPage/WaitlistModal/WaitlistModal'
import { EmbroideryService, useMakeEmbroideredCartItem } from '../../containers/EmbroideryService'
import { Variant } from '../../types/graphql'

export interface AddToBagErrors {
  missingRequiredSize: boolean
  missingRequiredLogoEmbroidery: boolean
  missingRequiredTextEmbroidery: boolean
}

interface Props {
  isPortalColorSelected?: boolean
  isProductFinalSale: boolean
  isProductWaitlistable: boolean
  requiresSizeSelection: boolean
  selectedVariant: Variant | null | undefined
  selectedSize: Size | undefined
  isProductEmbroiderable?: boolean
}

export interface Output {
  addToBag: (properties?: Record<string, string>) => void
  addToBagErrors: AddToBagErrors
  handleAddToBagClick: () => Promise<void>
  hasSizeErrors: boolean
  isAddToCartInFlight: boolean
  quantity: number
  resetInvalidBagClickStatus: () => void
  setQuantity: (quantity: number) => void
}

const INTERSTITIAL_PATH_PATTERN = /\/(products|kits)\/.+/

/**
 * @deprecated Use useCart, useCartDialog, VariantSelectionForm and maybe some embroidery utils if necessary.
 */
export function useBag({
  isProductFinalSale,
  isProductWaitlistable,
  requiresSizeSelection,
  selectedSize,
  selectedVariant,
  isPortalColorSelected,
  isProductEmbroiderable,
}: Props): Output {
  const { addItems, errorMessage: cartErrorMessage } = CartContainer.useContainer()
  const { open: openMiniCartDialog } = useCartDialog()
  const { open: openCartInterstitialDialog } = useCartInterstitialDialog()
  const embroideryService = EmbroideryService.useContainer()
  const { openWaitlistModal } = useWaitlistModal()
  const pathname = usePathname()
  const { hasMandatoryTextEmbroidery, hasMandatoryLogoEmbroidery, portal } = usePortal()
  const makeEmbroideredCartItem = useMakeEmbroideredCartItem()

  const isValidPathForInterstitial = pathname?.search(INTERSTITIAL_PATH_PATTERN) !== -1

  const cartInterstitialTestDecision = useFeatureFlag<
    'no-cart-interstitial' | 'yes-cart-interstitial'
  >({
    key: 'cart-interstitial-test',
    controlVariant: 'no-cart-interstitial',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: !isValidPathForInterstitial,
  })

  const shouldDisplayCartInterstitial = cartInterstitialTestDecision === 'yes-cart-interstitial'

  const mandatoryTextEmbroidery = hasMandatoryTextEmbroidery({
    categoryAnalyticsId: selectedVariant?.category ?? '',
  })
  const mandatoryLogoEmbroidery = hasMandatoryLogoEmbroidery({
    categoryAnalyticsId: selectedVariant?.category ?? '',
  })

  // Check portal embroidery settings against the currently confirmed embroidery selection
  const { isMissingRequiredLogoEmbroidery, isMissingRequiredTextEmbroidery } = useMemo(() => {
    const { selectedEmbroideryFormOptions, confirmAddEmbroidery } = embroideryService

    // Determine if portal embroidery is defined as required
    const isTextEmbroideryRequired =
      isPortalColorSelected && isProductEmbroiderable && mandatoryTextEmbroidery

    const isLogoEmbroideryRequired =
      isPortalColorSelected && isProductEmbroiderable && mandatoryLogoEmbroidery

    // Determine if selected embroidery items are valid for what is defined by the portal
    const isMissingRequiredTextEmbroidery =
      isTextEmbroideryRequired &&
      (!selectedEmbroideryFormOptions?.textFirstLine ||
        (!confirmAddEmbroidery && selectedEmbroideryFormOptions.textFirstLine))

    const isMissingRequiredLogoEmbroidery =
      isLogoEmbroideryRequired &&
      (!selectedEmbroideryFormOptions?.iconImageSource ||
        (!confirmAddEmbroidery && selectedEmbroideryFormOptions.iconImageSource))

    return { isMissingRequiredTextEmbroidery, isMissingRequiredLogoEmbroidery }
  }, [
    isProductEmbroiderable,
    mandatoryLogoEmbroidery,
    mandatoryTextEmbroidery,
    isPortalColorSelected,
    embroideryService,
  ])

  const [quantity, setQuantity] = useState<number>(1)

  const [addToBagErrors, setAddToBagErrors] = useState<AddToBagErrors>({
    missingRequiredSize: false,
    missingRequiredTextEmbroidery: false,
    missingRequiredLogoEmbroidery: false,
  })

  const [isAddToCartInFlight, setIsAddToCartInFlight] = useState<boolean>(false)

  const hasSizeErrors = useMemo(() => {
    if (requiresSizeSelection && !selectedSize) {
      return true
    }

    if (selectedSize?.soldOut && !isProductWaitlistable) {
      return true
    }

    return false
  }, [requiresSizeSelection, selectedSize, isProductWaitlistable])

  async function handleAddToBagClick(): Promise<void> {
    // Collect validation errors when ATC is clicked
    let errors: Partial<AddToBagErrors> = {}

    // If no size selected, scroll to size selector and set error
    if (hasSizeErrors) {
      errors.missingRequiredSize = true
      const sizeSectionTitle = document.getElementsByClassName(
        'size-section-title'
      )[0] as HTMLElement | null
      if (sizeSectionTitle) {
        try {
          sizeSectionTitle.scrollIntoView({ behavior: 'smooth', block: 'start' })
          sizeSectionTitle.focus({ preventScroll: true })
        } catch {}
      }
    }

    // If mandatory text embroidery is required for the user & no text embroidery is present
    if (isMissingRequiredTextEmbroidery) {
      errors.missingRequiredTextEmbroidery = true
    }

    // If mandatory logo embroidery is required for the user & no logo embroidery is present, manage error state
    if (isMissingRequiredLogoEmbroidery) {
      errors.missingRequiredLogoEmbroidery = true
    }

    // If any validation errors exist, update error state
    //
    // @TODO: This check should also take into consideration if there is a missing required logo embroidery,
    // but the current logic does not allow for us to add a logo through the modal without also adding text embroidery.
    // This needs to be revisited to properly handle the case where a portal only enforces mandatory text or logo embroidery individually.
    if (errors.missingRequiredSize || errors.missingRequiredTextEmbroidery) {
      setAddToBagErrors(prevErrors => ({
        ...prevErrors,
        ...errors,
      }))
    } else if (isProductWaitlistable && selectedSize && selectedSize.soldOut) {
      // if no errors & product is waitlistable, open waitlist modal
      openWaitlistModal()
    } else {
      // Otherwise, add the products to the cart
      await addToBag()
    }
  }

  const resetInvalidBagClickStatus = useCallback(() => {
    setAddToBagErrors({
      missingRequiredSize: false,
      missingRequiredLogoEmbroidery: false,
      missingRequiredTextEmbroidery: false,
    })
  }, [])

  async function addToBag(): Promise<void> {
    if (!selectedVariant) return

    setIsAddToCartInFlight(true)
    await addItems(
      makeEmbroideredCartItem(
        {
          item: selectedVariant,
          finalSale: isProductFinalSale,
          options: {
            properties: undefined,
            quantity,
            attributedPortal:
              isPortalColorSelected && portal?.name
                ? {
                    name: portal.name,
                    itemType: 'PortalColor',
                  }
                : undefined,
          },
        },
        selectedVariant.product.rawCategory ?? selectedVariant.category
      )
    )
    setIsAddToCartInFlight(false)

    // we only want to open the interstitial or minicart if no errors occurred on `addItems`
    if (!cartErrorMessage) {
      if (shouldDisplayCartInterstitial) {
        openCartInterstitialDialog()
      } else {
        openMiniCartDialog()
      }
    }
  }

  return {
    addToBag,
    addToBagErrors,
    handleAddToBagClick,
    hasSizeErrors,
    isAddToCartInFlight,
    quantity,
    resetInvalidBagClickStatus,
    setQuantity,
  }
}
